import type { BroadcastNotificationPayload } from "@/entities/broadcast-notification";
import type { LiteralObject } from "@/types";

import { useEffect } from "react";

import { isDebugModeActivated } from "@/config/environmentUtils";
import { useGetCurrentUser } from "@/entities/current-user";
import { useEventBus } from "@/modules/event-bus";
import { useAuthContext } from "@/services/auth";
import { useBroadcastContext } from "@/services/broadcast";

import {
  BROADCAST_EVENT,
  PRIVATE_USER_BROADCAST_CHANNEL,
} from "./app-broadcasting.constant";

const AppBroadcastingHandler = () => {
  const { pusher } = useBroadcastContext();
  const { currentUser } = useGetCurrentUser();
  const { hasToken } = useAuthContext();
  const eventBus = useEventBus();

  const channel = pusher?.subscribe(
    `${PRIVATE_USER_BROADCAST_CHANNEL}${currentUser?.id}`
  );

  const userMessageReceivedCallback = (
    payload: BroadcastNotificationPayload
  ) => {
    eventBus.emit(payload.event_type, payload);
  };

  const logAllEvents = (event: string, data: LiteralObject) => {
    if (!isDebugModeActivated()) return;

    // Log all events and data in debug mode
    console.info(event, data);
  };

  useEffect(() => {
    // User must be logged in to receive broadcasted notifications
    if (!pusher || !channel || !currentUser?.id || !hasToken) return;

    channel.bind(BROADCAST_EVENT, userMessageReceivedCallback);
    channel.bind_global(logAllEvents);
    return () => {
      channel.unbind(BROADCAST_EVENT, userMessageReceivedCallback);
      channel.unbind_global(logAllEvents);
    };
  }, [pusher, channel, currentUser?.id, hasToken]);

  return null;
};

export default AppBroadcastingHandler;
